import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  FaGasPump, 
  FaClipboardList, 
  FaCheckCircle,
  FaSpinner,
  FaSyncAlt,
  FaCar,
  FaUser,
  FaCalendarAlt,
  FaClock,
  FaTint,
  FaMapMarkerAlt,
  FaArrowRight,
  FaChartLine,
  FaTimesCircle
} from 'react-icons/fa';
import { MdVerified, MdLocalGasStation } from 'react-icons/md';
import { BiSolidDashboard } from 'react-icons/bi';
import '../../styles/Dashboard.css';

const HomeScreen = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading, tokenReady, apiService } = useAuth();
  const [solicitacoes, setSolicitacoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [solicitacaoCancelada, setSolicitacaoCancelada] = useState(null);
  const [lastActivity, setLastActivity] = useState(Date.now());

  async function carregarSolicitacoes() {
    try {
      const response = await apiService.getSolicitacoes({ 
        status: 'pendente',
        pagina: 0,
        limite: 10 
      });
      
      if (response.sucesso && response.solicitacoes) {
        setSolicitacoes(response.solicitacoes);
      } else {
        console.log('Erro ao carregar solicitacoes:', response.mensagem);
      }
    } catch (error) {
      console.error('Erro:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }

  useEffect(() => {
    if (!authLoading && tokenReady) {
      console.log('Home - Token pronto, carregando solicitacoes...');
      carregarSolicitacoes();
    }
  }, [authLoading, tokenReady]);

  // Verificar a atividade do usuário
  useEffect(() => {
    const handleActivity = () => {
      setLastActivity(Date.now());
    };

    window.addEventListener('mousemove', handleActivity);
    window.addEventListener('keydown', handleActivity);
    window.addEventListener('click', handleActivity);
    window.addEventListener('touchstart', handleActivity);
    window.addEventListener('scroll', handleActivity);

    return () => {
      window.removeEventListener('mousemove', handleActivity);
      window.removeEventListener('keydown', handleActivity);
      window.removeEventListener('click', handleActivity);
      window.removeEventListener('touchstart', handleActivity);
      window.removeEventListener('scroll', handleActivity);
    };
  }, []);

  const onRefresh = useCallback(() => {
    if (!tokenReady) return;
    setRefreshing(true);
    carregarSolicitacoes();
  }, [tokenReady]);

  // Auto-refresh a cada 1 minuto se não houver atividade
  useEffect(() => {
    const interval = setInterval(() => {
      const timeSinceActivity = Date.now() - lastActivity;
      if (timeSinceActivity >= 60000 && tokenReady && !refreshing && !authLoading) {
        setLastActivity(Date.now());
        onRefresh();
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [lastActivity, tokenReady, refreshing, authLoading, onRefresh]);

  function formatarCodigoSolicitacao(id) {
    const apenasDigitos = String(id ?? '').replace(/\D/g, '');
    const base = apenasDigitos || '0';
    return base.padStart(6, '0').slice(-6);
  }

  async function handleAbastecer(solicitacao) {
    console.log('Home - Clicou em Abastecer, solicitacao:', solicitacao);
    // navigate(`/validando?id=${solicitacao.id}`);

    try {
      // setStatus('Buscando detalhes da solicitação...');
      console.log('Validando - Buscando detalhes da solicitação ID:', solicitacao.id);
      
      const response = await apiService.getSolicitacaoDetalhe(Number(solicitacao.id));
      console.log('Validando - Resposta:', response);

      if (response.sucesso && response.solicitacao) {
        if (response.solicitacao.cancelado) {
          setSolicitacaoCancelada(response.solicitacao);
        } else {
          navigate(`/registrarAbastecimento?solicitacaoId=${response.solicitacao.id}&dados=${encodeURIComponent(JSON.stringify(response.solicitacao))}`);
        }
        
      } else {
        alert(`Atenção: ${response.mensagem || 'Solicitação não encontrada'}`);
      }
    } catch (error) {
      console.error('Erro ao buscar solicitação:', error);
      alert('Erro ao validar solicitação');
    }
  }

  // Card de estatísticas
  const StatsCard = ({ title, value, icon, color }) => (
    <div className="stats-card">
      <div className="stats-icon" style={{ backgroundColor: color }}>
        {icon}
      </div>
      <div className="stats-info">
        <h3>{value}</h3>
        <p>{title}</p>
      </div>
    </div>
  );

  // Componente de solicitação
  const SolicitacaoCard = ({ solicitacao }) => {
    return (
      <div className="solicitacao-card-web">
        <div className="card-status">
          <div className="status-badge">
            <MdVerified className="status-icon-dash" />
            <span>{solicitacao.status || '-'}</span>
          </div>
          <span className="codigo-solicitacao-status">
            <span className="codigo-solicitacao-titulo">CÓDIGO DE AUTORIZAÇÃO</span>
            <span className="codigo-solicitacao-numero">{formatarCodigoSolicitacao(solicitacao.id)}</span>
          </span>
          <div className="card-actions">
            <button 
              className="btn-abastecer-web"
              onClick={() => handleAbastecer(solicitacao)}
            >
              <FaGasPump />
              <span>Abastecer</span>
              <FaArrowRight />
            </button>
          </div>
        </div>

        <div className="card-header-web">
          <h3 className="orgao-nome">
            {solicitacao.orgao || 'Órgão não informado'}
          </h3>
        </div>

        <div className="card-info-row">
            {/*
          <div className="info-item-web">
            <FaUser className="info-icon" />
            <div>
              <span className="info-label">Motorista</span>
              <span className="info-value">{solicitacao.motorista?.nome || 'Não informado'}</span>
              </div>
              </div>
            */}
            
          <div className="info-item-web">
            <FaCar className="info-icon" />
            <div>
              <span className="info-label">Placa</span>
              <span className="info-value">{solicitacao.veiculo?.placa || '-'}</span>
            </div>
          </div>
        </div>

        <div className="card-details">
          <div className="detail-item">
            <FaCalendarAlt className="detail-icon" />
            <span>{solicitacao.data_formatada || '-'}</span>
          </div>
          <div className="detail-item">
            <FaClock className="detail-icon" />
            <span>{solicitacao.hora_formatada || '-'}</span>
          </div>
          <div className="detail-item">
            <FaTint className="detail-icon" />
            <span>{solicitacao.combustivel || '-'}</span>
          </div>
        </div>

        <div className="card-footer-web">
          <div className="veiculo-info">
            <span className="veiculo-modelo">
              {solicitacao.veiculo?.descricao || 'Veículo não informado'}
            </span>
          </div>
        </div>
      </div>
    );
  };

    // Modal de solicitação cancelada
  const SolicitacaoCanceladaModal = ({ show, onClose, solicitacao }) => {
    if (!show) return null;

    return (
      <>
        <div className="cancelada-modal-overlay" onClick={onClose}>
          <div className="cancelada-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="cancelada-modal-header">
              <div className="cancelada-header-info">
                <FaTimesCircle className="cancelada-icon" />
                <h2>Solicitação Cancelada pelo Motorista</h2>
              </div>
              <button className="cancelada-close-btn" onClick={onClose}>
                <FaTimesCircle />
              </button>
            </div>

            <div className="cancelada-modal-body">
              <div className="cancelada-info">
                <span className="cancelada-label">Solicitação N°:</span>
                <span className="cancelada-value">{formatarCodigoSolicitacao(solicitacao.id)}</span>
              </div>
              <div className="cancelada-info">
                <span className="cancelada-label">Data/Hora da solicitação:</span>
                <span className="cancelada-value">{solicitacao.data_formatada || '-'} {solicitacao.hora_formatada || '-'}</span>
              </div>
              <div className="cancelada-info">
                <span className="cancelada-label">Placa:</span>
                <span className="cancelada-value">{solicitacao.veiculo?.placa || '-'}</span>
              </div>
              <div className="cancelada-info">
                <span className="cancelada-label">CPF:</span>
                <span className="cancelada-value">{solicitacao.motorista?.cpf || '-'}</span>
              </div>
              <div className="cancelada-info">
                <span className="cancelada-label">Data/Hora cancelamento:</span>
                <span className="cancelada-value">{solicitacao.data_cancelamento || '-'} {solicitacao.hora_cancelamento || '-'}</span>
              </div>
            </div>

            <div className="cancelada-modal-footer">
              <button className="cancelada-btn-fechar" onClick={onClose}>
                <FaTimesCircle />
                <span>Fechar</span>
              </button>
            </div>
          </div>
        </div>
      </>
    );
  };

  // Estatísticas
  const stats = {
    totalSolicitacoes: solicitacoes.length,
    concluidasHoje: 0,
    emAndamento: solicitacoes.length,
  };

  return (
    <div className="dashboard-web">
      <div className="welcome-section">
        <div className="welcome-text">
          <h1>
            Olá, <span className="user-name">{user?.nome || 'Frentista'}</span>
          </h1>
          <p>Gerencie os abastecimentos e solicitações pendentes</p>
        </div>
        <div className="welcome-stats">
          <FaClipboardList />
          <span>{stats.totalSolicitacoes} pendente(s)</span>
        </div>
      </div>

      {/* Posto Card */}
      <div className="posto-card-web">
        <div className="posto-icon-web">
          <FaMapMarkerAlt />
        </div>
        <div className="posto-content">
          <div className="posto-header">
            <h3>{user?.posto?.nome || 'Posto não vinculado'}</h3>
            {user?.posto?.ativo && (
              <span className="ativo-badge-web">Ativo</span>
            )}
          </div>
          <p className="posto-endereco-web">{user?.posto?.endereco || ''}</p>
        </div>
      </div>

      {/* Card de solicitação cancelada */}
      {solicitacaoCancelada && (
        <SolicitacaoCanceladaModal 
          show={!!solicitacaoCancelada} 
          onClose={() => setSolicitacaoCancelada(null)} 
          solicitacao={solicitacaoCancelada} 
        />
      )}

      {/* Solicitações */}
      <div className="solicitacoes-section">
        <div className="section-header">
          <h2>
            <FaGasPump className="section-icon" />
            Para abastecer
            {solicitacoes.length > 0 && (
              <span className="badge-count">{solicitacoes.length}</span>
            )}
          </h2>
          <button 
            className="refresh-btn-web" 
            onClick={onRefresh}
            disabled={refreshing || authLoading || !tokenReady}
          >
            <FaSyncAlt className={refreshing ? 'spin' : ''} />
            <span>{refreshing ? 'Atualizando...' : 'Atualizar'}</span>
          </button>
        </div>

        {/* Loading */}
        {authLoading || !tokenReady ? (
          <div className="loading-state">
            <div className="loading-spinner-web"></div>
            <p>{authLoading ? 'Inicializando...' : 'Carregando token...'}</p>
          </div>
        ) : loading ? (
          <div className="loading-state">
            <div className="loading-spinner-web"></div>
            <p>Carregando solicitações...</p>
          </div>
        ) : solicitacoes.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">✅</div>
            <h3>Nenhuma solicitação pendente</h3>
            <p>Todas as solicitações foram atendidas!</p>
            <button className="empty-refresh-btn" onClick={onRefresh}>
              <FaSyncAlt />
              <span>Atualizar</span>
            </button>
          </div>
        ) : (
          <div className="solicitacoes-grid">
            {solicitacoes.map((solicitacao) => (
              <SolicitacaoCard key={solicitacao.id} solicitacao={solicitacao} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default HomeScreen;